import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Building2, Calendar, ClipboardCheck, TrendingUp, BarChart3, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_COLORS = {
  'daily-walkthrough': { color: '#3b82f6', bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', label: 'Operations' },
  'self-assessment': { color: '#22c55e', bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', label: 'Quality' },
  'bca': { color: '#f97316', bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', label: 'Brand' },
  'steritech': { color: '#ef4444', bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', label: 'Health & Safety' },
  'be-clean': { color: '#8b5cf6', bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-700', label: 'Cleanliness' }
};

export default function Reports() {
  const { user } = useAuth();
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState('all');
  const [data, setData] = useState(null);
  const [storeReports, setStoreReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');

  useEffect(() => { fetchData(); }, [selectedStore, dateRange]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const storesRes = await axios.get(`${API_URL}/stores`);
      setStores(storesRes.data);

      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - parseInt(dateRange) * 86400000).toISOString().split('T')[0];
      const params = { start_date: startDate, end_date: endDate };
      if (selectedStore !== 'all') params.store_id = selectedStore;

      const compRes = await axios.get(`${API_URL}/reports/compliance`, { params });
      setData(compRes.data);

      if (user?.role === 'owner') {
        const storeRes = await axios.get(`${API_URL}/reports/stores`);
        setStoreReports(storeRes.data);
      }
    } catch {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-slate-500" data-testid="loading">Loading reports...</div>;
  }

  const totalAudits = data?.total_audits || 0;
  const completedAudits = data?.completed_audits || 0;
  const avgScore = data?.average_score || 0;
  const typeBreakdown = data?.audit_type_breakdown || [];
  const incompleteAudits = data?.incomplete_audits || [];

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Audit Reports
        </h1>
        <p className="text-slate-500 text-sm mt-1">Daily audit performance across your stores</p>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        {user?.role === 'owner' && stores.length > 0 && (
          <Select value={selectedStore} onValueChange={setSelectedStore}>
            <SelectTrigger className="w-48 rounded-xl border-slate-200" data-testid="store-filter">
              <Building2 className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue placeholder="All Stores" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stores</SelectItem>
              {stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={dateRange} onValueChange={setDateRange}>
          <SelectTrigger className="w-44 rounded-xl border-slate-200" data-testid="date-range-filter">
            <Calendar className="w-4 h-4 mr-2 text-slate-400" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="14">Last 14 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="border border-slate-200 rounded-xl bg-white" data-testid="stat-total">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-blue-50 rounded-xl flex items-center justify-center">
                <ClipboardCheck className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-xl font-bold text-slate-900">{totalAudits}</p>
                <p className="text-[11px] text-slate-500">Total Audits</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 rounded-xl bg-white" data-testid="stat-completed">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-green-50 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="text-xl font-bold text-green-600">{completedAudits}</p>
                <p className="text-[11px] text-slate-500">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-red-100 rounded-xl bg-red-50/30" data-testid="stat-missed">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-red-100 rounded-xl flex items-center justify-center">
                <AlertCircle className="w-4 h-4 text-red-500" />
              </div>
              <div>
                <p className="text-xl font-bold text-red-500">{totalAudits - completedAudits}</p>
                <p className="text-[11px] text-slate-500">Incomplete</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-200 rounded-xl bg-white" data-testid="stat-score">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${avgScore >= 80 ? 'bg-green-50' : avgScore >= 50 ? 'bg-amber-50' : 'bg-red-50'}`}>
                <BarChart3 className={`w-4 h-4 ${avgScore >= 80 ? 'text-green-600' : avgScore >= 50 ? 'text-amber-600' : 'text-red-600'}`} />
              </div>
              <div>
                <p className={`text-xl font-bold ${avgScore >= 80 ? 'text-green-600' : avgScore >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{avgScore}%</p>
                <p className="text-[11px] text-slate-500">Avg Score</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Audit Type Breakdown */}
      <Card className="border border-slate-200 rounded-xl" data-testid="type-breakdown">
        <CardContent className="p-5">
          <h2 className="text-base font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>By Audit Type</h2>
          {typeBreakdown.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {typeBreakdown.map(t => {
                const tc = TYPE_COLORS[t.audit_type] || { color: '#64748b', bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700', label: 'Other' };
                return (
                  <div key={t.audit_type} className={`${tc.bg} ${tc.border} border rounded-lg p-4`}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: tc.color }} />
                      <span className={`text-[10px] font-semibold uppercase tracking-wider ${tc.text}`}>{tc.label}</span>
                    </div>
                    <p className="text-sm font-medium text-slate-800 mb-2">{t.label}</p>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-slate-500">{t.completed}/{t.total} completed</span>
                      <span className={`text-sm font-bold ${t.avg_score >= 80 ? 'text-green-600' : t.avg_score >= 50 ? 'text-amber-600' : 'text-slate-400'}`}>
                        {t.avg_score > 0 ? `${t.avg_score}%` : '--'}
                      </span>
                    </div>
                    <div className="w-full h-2 bg-white/80 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${t.total > 0 ? (t.completed / t.total) * 100 : 0}%`, backgroundColor: tc.color }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-center text-slate-400 py-6 text-sm">No audits performed yet</p>
          )}
        </CardContent>
      </Card>

      {/* Incomplete Audits - Accountability */}
      {incompleteAudits.length > 0 && (
        <Card className="border border-red-200 rounded-xl bg-red-50/20" data-testid="incomplete-audits">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <AlertCircle className="w-4 h-4 text-red-500" />
              <h2 className="text-base font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Incomplete Audits ({incompleteAudits.length})
              </h2>
            </div>
            <div className="space-y-2">
              {incompleteAudits.map(audit => (
                <div key={audit.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-red-100">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-red-400" />
                    <div>
                      <p className="text-sm font-medium text-slate-800">{audit.audit_type}</p>
                      <p className="text-xs text-slate-400">{audit.store_name} &middot; {audit.started_by}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-[10px] px-2 py-0.5 bg-red-100 text-red-600 rounded-full font-medium uppercase">Not Completed</span>
                    <p className="text-[11px] text-slate-400 mt-0.5">{audit.date}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Store Comparison (Owner only) */}
      {user?.role === 'owner' && storeReports.length > 0 && (
        <Card className="border border-slate-200 rounded-xl" data-testid="store-comparison">
          <CardContent className="p-5">
            <h2 className="text-base font-bold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Store Performance</h2>
            <div className="space-y-2">
              {storeReports.map(store => (
                <div key={store.store_id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors">
                  <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-4 h-4 text-slate-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-medium text-slate-800 truncate block">{store.store_name}</span>
                    <span className="text-xs text-slate-400">{store.city}</span>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className="text-xs text-slate-400">{store.completed_audits}/{store.total_audits} audits</span>
                    <span className={`ml-3 text-sm font-bold ${store.avg_score >= 80 ? 'text-green-600' : store.avg_score >= 50 ? 'text-amber-600' : 'text-slate-400'}`}>
                      {store.avg_score > 0 ? `${store.avg_score}%` : '--'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
